import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import XLSX from 'xlsx-js-style';
import { saveAs } from 'file-saver';
import MobilePageLayout from '../../../components/layout/MobilePageLayout';
import { convertToVwMob } from '../../../Utils/responsive';
import { usePaymentStatus } from '../hooks/usePaymentStatus';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { selectGetProjectStatus, selectProjects, selectProjectsStatus, selectSelectedProject } from '../../../Module/Project/Presentation/projectSelectors';
import { selectSession } from '../../../Module/Auth/Presentation/authSelectors';
import { selectUserProfile } from '../../../Module/User/Presentation/userSelectors';
import { getProject, searchProjects } from '../../../Module/Project/Presentation/projectActions';
import ProjectDetailHeader from '../../../components/common/ProjectDetailHeader';
import DSLoading from '../../../components/designSystem/DSLoading';
import DSBottomSheet from '../../../components/designSystem/DSBottomSheet';
import { useTranslation } from 'react-i18next';

import defaultProfileIcon from '../../../assets/Icon/project/default-profile.png';
import kakaoIcon from '../../../assets/Icon/project/kakao_icon.png';
import emailIcon from '../../../assets/Icon/project/email_icon.png';
import { openMailClient } from '../../../Utils/mail';
import { buildSupporterMessageMailTemplate } from '../../../Utils/shareMailTemplates';
import { settlementUseCases } from '../../../Module/Settlement/Presentation/settlementUseCases';
import { getProjectShareImageUrl, shareKakaoFeed } from '../../../Utils/kakaoShare';
import { formatUserFullName } from '../../../Utils/formatUserName';
import { PATHS } from '../../../routes/paths';
import { formatDisplayCurrency } from '../../../Utils/formatCurrency';

const ITEM_HEIGHT = 46; // vw 기반 높이 (숫자만 사용)

const PaymentStatusMob = () => {
  const { t, i18n } = useTranslation('translation');
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const selectedProject = useAppSelector(selectSelectedProject);
  const projects = useAppSelector(selectProjects);
  const projectsStatus = useAppSelector(selectProjectsStatus);
  const session = useAppSelector(selectSession);
  const userProfile = useAppSelector(selectUserProfile);
  const getProjectStatus = useAppSelector(selectGetProjectStatus);
  const projectId = (location.state as { projectId?: string } | undefined)?.projectId;
  const userId = (session as any)?.user?.id || (typeof window !== 'undefined' ? window.localStorage.getItem('missionfund.userId') : null) || '';

  useEffect(() => {
    if (!projectId && userId && projects.length === 0 && projectsStatus !== 'loading') {
      dispatch(searchProjects({ limit: 100, params: [{ fld: 'user_id', op: 'eq', val: userId }], sort: 'created_at:desc' }));
    }
  }, [dispatch, projectId, userId, projects.length, projectsStatus]);

  useEffect(() => {
    if (!projectId && projectsStatus === 'succeeded' && projects.length === 0) {
      navigate(PATHS.PROJECT_MANAGEMENT, { replace: true });
    }
  }, [projectId, projectsStatus, projects.length, navigate]);

  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth() + 1;
  const currentDay = today.getDate();
  const minYear = 2025;
  const years = Array.from({ length: Math.max(currentYear - minYear + 1, 1) }, (_, index) => currentYear - index);
  const months = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
  const statuses = ['all', 'complete', 'fail'] as const;
  const getLatestAvailableYearMonth = (year: number, month: number, day: number) => {
    let adjustedYear = year;
    let adjustedMonth = day >= 20 ? month - 1 : month - 2;
    while (adjustedMonth <= 0) {
      adjustedMonth += 12;
      adjustedYear -= 1;
    }
    return { year: adjustedYear, month: adjustedMonth };
  };
  const { year: latestAvailableYear, month: latestAvailableMonth } = getLatestAvailableYearMonth(currentYear, currentMonth, currentDay);
  const selectableYears = years.filter((year) => year <= latestAvailableYear);

  const getMaxSelectableMonth = (year: number) => {
    if (year < latestAvailableYear) return 12;
    if (year === latestAvailableYear) return latestAvailableMonth;
    return 0;
  };

  const getMinSelectableMonth = (year: number) => {
    if (year === 2025) return 3;
    return 1;
  };

  const getDefaultYearMonth = () => {
    if (selectableYears.length > 0) {
      const defaultYear = selectableYears[0];
      return {
        year: defaultYear,
        month: getMaxSelectableMonth(defaultYear),
      };
    }
    return {
      year: years[0],
      month: 1,
    };
  };
  const defaultYearMonth = getDefaultYearMonth();

  useEffect(() => {
    if (projectId) {
      if (!selectedProject || selectedProject.project_id !== projectId) {
        dispatch(getProject({ project_id: projectId }));
      }
    }
  }, [dispatch, projectId, selectedProject]);

  const currentProject = useMemo(() => {
    if (projectId) {
      if (selectedProject?.project_id === projectId) return selectedProject;
      const found = projects.find((p) => p.project_id === projectId);
      return found ?? selectedProject;
    }
    return selectedProject || projects[0];
  }, [projectId, selectedProject, projects]);
  const projectTitle = currentProject?.title || '';
  const fallbackFullName = formatUserFullName(
    (session as { user?: { first_name?: string } } | undefined)?.user?.first_name,
    (session as { user?: { last_name?: string } } | undefined)?.user?.last_name,
  );
  const userFullName =
    (session as { user?: { name?: string } } | undefined)?.user?.name ??
    (fallbackFullName ? fallbackFullName : undefined) ??
    (session as { user?: { email?: string } } | undefined)?.user?.email ??
    '';
  const effectiveProjectId = projectId ?? currentProject?.project_id;
  const [activeTab, setActiveTab] = useState<'summary' | 'details'>('summary');
  const [selectedYear, setSelectedYear] = useState<number>(defaultYearMonth.year);
  const [selectedMonth, setSelectedMonth] = useState<number>(defaultYearMonth.month);
  const [selectedStatus, setSelectedStatus] = useState<'all' | 'complete' | 'fail'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [isShareBottomSheetOpen, setIsShareBottomSheetOpen] = useState(false);
  const [isCheckingPreviousMonth, setIsCheckingPreviousMonth] = useState(true);
  const [hasPreviousMonthData, setHasPreviousMonthData] = useState(false);
  
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [isStatusPickerOpen, setIsStatusPickerOpen] = useState(false);
  
  // 임시 선택 상태 (피커 내에서만 사용)
  const [tempYear, setTempYear] = useState<number>(selectedYear);
  const [tempMonth, setTempMonth] = useState<number>(selectedMonth);
  const [tempStatus, setTempStatus] = useState<'all' | 'complete' | 'fail'>(selectedStatus);

  const yearRef = useRef<HTMLDivElement>(null);
  const monthRef = useRef<HTMLDivElement>(null);

  const isEnglish = /^en/i.test(i18n.resolvedLanguage || i18n.language || '');
  const englishMonthNames = useMemo(
    () => ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
    [],
  );
  const yearLabel = (year: number) => (isEnglish ? `${year}` : `${year}${t('common.units.yearSuffix')}`);
  const monthLabel = (month: number) => (isEnglish ? englishMonthNames[month - 1] : `${month}${t('common.units.monthSuffix')}`);
  const statusLabel = (status: typeof statuses[number]) => (status === 'all' ? t('common.filters.all') : t(`common.payment.status.${status}`));
  const selectableMonthsForTempYear = months.filter((month) => {
    return month >= getMinSelectableMonth(tempYear) && month <= getMaxSelectableMonth(tempYear);
  });

  // API 연동 훅 사용
  const {
    summary,
    details,
    hasData,
    isLoading,
    summaryStatus,
    detailsStatus,
    fetchPaymentDetailsAllPages,
    resolvePaymentDisplayAmount,
    summaryDisplayCurrency,
  } = usePaymentStatus(selectedYear, selectedMonth, currentProject?.project_id);

  const isProjectLoading =
    Boolean(projectId) &&
    currentProject?.project_id !== projectId &&
    (getProjectStatus === 'idle' || getProjectStatus === 'loading');
  const isInitialSettlementLoading =
    Boolean(currentProject?.project_id) &&
    !hasData &&
    (summaryStatus === 'idle' || detailsStatus === 'idle');
  const shouldShowLoading = isProjectLoading || isLoading || isInitialSettlementLoading || (!hasData && isCheckingPreviousMonth);

  useEffect(() => {
    if (!currentProject?.project_id) {
      setIsCheckingPreviousMonth(false);
      setHasPreviousMonthData(false);
      return;
    }

    setIsCheckingPreviousMonth(true);
    setHasPreviousMonthData(false);
  }, [currentProject?.project_id, selectedMonth, selectedYear]);

  useEffect(() => {
    const projectIdForCheck = currentProject?.project_id;
    if (!projectIdForCheck) {
      setIsCheckingPreviousMonth(false);
      setHasPreviousMonthData(false);
      return;
    }

    if (isLoading) {
      return;
    }

    if (hasData) {
      setIsCheckingPreviousMonth(false);
      setHasPreviousMonthData(false);
      return;
    }

    const prevYear = selectedMonth === 1 ? selectedYear - 1 : selectedYear;
    const prevMonth = selectedMonth === 1 ? 12 : selectedMonth - 1;
    const minSelectableMonthAtMinYear = 3;
    if (prevYear < minYear || (prevYear === minYear && prevMonth < minSelectableMonthAtMinYear)) {
      setIsCheckingPreviousMonth(false);
      setHasPreviousMonthData(false);
      return;
    }

    const monthStr = String(prevMonth).padStart(2, '0');
    const lastDay = String(new Date(prevYear, prevMonth, 0).getDate()).padStart(2, '0');
    const startDate = `${prevYear}-${monthStr}-01`;
    const endDate = `${prevYear}-${monthStr}-${lastDay}`;

    let cancelled = false;
    setIsCheckingPreviousMonth(true);

    void Promise.all([
      settlementUseCases.getCommissionSum.execute({
        limit: 100,
        params: [
          { fld: 'projectId', val: projectIdForCheck, op: 'eq' },
          { fld: 'pay_date', op: 'between', val: startDate, v2: endDate },
        ],
        local_field: 'projectId',
        from: 'projects',
      }),
      settlementUseCases.searchCommissions.execute({
        params: [
          { fld: 'projectId', val: projectIdForCheck, op: 'eq' },
          { fld: 'pay_date', op: 'between', val: startDate, v2: endDate },
        ],
        local_field: 'projectId',
        from: 'projects',
        sort: 'pay_date:desc',
        limit: 100,
      }),
    ])
      .then(([sumResponse, detailResponse]) => {
        if (cancelled) return;
        const previousHasData =
          (sumResponse?.extra?.project_count ?? 0) > 0 ||
          (detailResponse?.data?.length ?? 0) > 0;
        setHasPreviousMonthData(previousHasData);
      })
      .catch(() => {
        if (cancelled) return;
        setHasPreviousMonthData(true);
      })
      .finally(() => {
        if (cancelled) return;
        setIsCheckingPreviousMonth(false);
      });

    return () => {
      cancelled = true;
    };
  }, [currentProject?.project_id, hasData, isLoading, minYear, selectedMonth, selectedYear]);

  const showPreparingEmptyState = !hasData && hasPreviousMonthData;

  // 초기 스크롤 위치 설정
  useEffect(() => {
    if (isDatePickerOpen) {
      setTimeout(() => {
        const yearIndex = selectableYears.indexOf(tempYear);
        const monthIndex = selectableMonthsForTempYear.findIndex((month) => month === tempMonth);
        
        const itemHeightPx = window.innerWidth * ITEM_HEIGHT / 393;
        
        if (yearRef.current) {
          yearRef.current.scrollTop = yearIndex * itemHeightPx;
        }
        if (monthRef.current) {
          monthRef.current.scrollTop = monthIndex * itemHeightPx;
        }
      }, 0);
    }
  }, [isDatePickerOpen, tempMonth, tempYear, selectableYears, selectableMonthsForTempYear]);

  const handleYearScroll = () => {
    if (!yearRef.current) return;
    const scrollPos = yearRef.current.scrollTop;
    const itemHeightPx = window.innerWidth * ITEM_HEIGHT / 393;
    const index = Math.round(scrollPos / itemHeightPx);
    if (index >= 0 && index < selectableYears.length && selectableYears[index] !== tempYear) {
      const nextYear = selectableYears[index];
      setTempYear(nextYear);
      const minM = getMinSelectableMonth(nextYear);
      const maxM = getMaxSelectableMonth(nextYear);
      setTempMonth((prevMonth) => Math.max(minM, Math.min(prevMonth, maxM)));
    }
  };

  const handleMonthScroll = () => {
    if (!monthRef.current) return;
    const scrollPos = monthRef.current.scrollTop;
    const itemHeightPx = window.innerWidth * ITEM_HEIGHT / 393;
    const index = Math.round(scrollPos / itemHeightPx);
    if (index >= 0 && index < selectableMonthsForTempYear.length) {
      const selected = selectableMonthsForTempYear[index];
      if (selected !== tempMonth) {
        setTempMonth(selected);
      }
    }
  };

  // 데이터 유무는 API에서 가져온 값 사용 (hasData from usePaymentStatus)

  const formatSummaryCurrency = useCallback(
    (displayValue: number) => {
      if (summaryDisplayCurrency === 'USD') {
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(displayValue);
      }
      return formatDisplayCurrency(displayValue, 'KRW');
    },
    [summaryDisplayCurrency],
  );

  const formatCurrency = useCallback(
    (amount: number) => {
      const { value, currency } = resolvePaymentDisplayAmount(amount);
      if (currency === 'USD') {
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
      }
      return formatDisplayCurrency(value, 'KRW');
    },
    [resolvePaymentDisplayAmount],
  );

  const shareToKakao = (url: string) => {
    const kakao = (window as any).Kakao;
    if (!(kakao?.Share?.sendDefault ?? kakao?.Link?.sendDefault)) {
      return;
    }
    const restrictedRegion = userProfile?.restricted_region === true || String(userProfile?.restricted_region) === 'true';
    shareKakaoFeed(kakao, {
      url,
      title: projectTitle || t('common.shareModal.defaultTitle'),
      imageUrl: getProjectShareImageUrl(currentProject as { image_url?: string; video_url?: string }),
      buttonTitle: t('common.shareModal.buttonDetail'),
      fundraiserName: userFullName || (currentProject as any)?.user_full_name || (currentProject as any)?.fundraiser_name || t('common.share.fundraiserFallback'),
      restrictedRegion,
    });
  };

  const handleCloseShareBottomSheet = () => {
    setIsShareBottomSheetOpen(false);
  };

  const runKakaoShare = () => {
    const KAKAO_KEY = 'd7fe97bd56b8e854e11feab6313e4b0a';
    const shareUrl = `https://go.missionfund.org/${effectiveProjectId || ''}`;

    if (!(window as any).Kakao) {
      const script = document.createElement('script');
      script.src = 'https://t1.kakaocdn.net/kakao_js_sdk/2.6.0/kakao.min.js';
      script.crossOrigin = 'anonymous';
      script.onload = () => {
        if (!(window as any).Kakao.isInitialized()) {
          (window as any).Kakao.init(KAKAO_KEY);
        }
        shareToKakao(shareUrl);
      };
      document.head.appendChild(script);
    } else {
      if (!(window as any).Kakao.isInitialized()) {
        (window as any).Kakao.init(KAKAO_KEY);
      }
      shareToKakao(shareUrl);
    }

  };

  const runEmailShare = () => {
    const projectUrlForMail = `https://go.missionfund.org/${effectiveProjectId || ''}`;
    const { subject, body } = buildSupporterMessageMailTemplate({
      fundraiserName:
        userFullName ||
        (currentProject as any)?.user_full_name ||
        (currentProject as any)?.fundraiser_name ||
        '',
      projectUrl: projectUrlForMail,
    });
    openMailClient({ subject, body });
  };

  const handleKakaoShareOption = () => {
    setIsShareBottomSheetOpen(false);
    runKakaoShare();
  };

  const handleEmailShareOption = () => {
    setIsShareBottomSheetOpen(false);
    runEmailShare();
  };

  const handleCopyLink = async () => {
    const url = `https://go.missionfund.org/${effectiveProjectId || ''}`;
    try {
      await navigator.clipboard.writeText(url);
      alert(t('common.messages.linkCopied'));
    } catch {
      const ta = document.createElement('textarea');
      ta.value = url;
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      alert(t('common.messages.linkCopied'));
    }
    handleCloseShareBottomSheet();
  };

  const isNextDisabled =
    selectedYear > latestAvailableYear ||
    (selectedYear === latestAvailableYear && selectedMonth >= latestAvailableMonth) ||
    (selectedMonth === 12 && !selectableYears.includes(selectedYear + 1));
  const isPrevDisabled = selectedYear === selectableYears[selectableYears.length - 1] && selectedMonth === 1;

  const handlePrevMonth = () => {
    if (isPrevDisabled) return;

    if (selectedMonth === 1) {
      const prevYear = selectedYear - 1;
      if (selectableYears.includes(prevYear)) {
        setSelectedYear(prevYear);
        setSelectedMonth(12);
      }
      return;
    }

    setSelectedMonth(selectedMonth - 1);
  };

  const handleNextMonth = () => {
    if (isNextDisabled) return;

    if (selectedMonth === 12) {
      const nextYear = selectedYear + 1;
      if (selectableYears.includes(nextYear)) {
        setSelectedYear(nextYear);
        setSelectedMonth(1);
      }
      return;
    }

    setSelectedMonth(selectedMonth + 1);
  };

  const handleDateClick = () => {
    setTempYear(selectedYear);
    setTempMonth(Math.min(selectedMonth, getMaxSelectableMonth(selectedYear)));
    setIsDatePickerOpen(true);
  };

  const handleConfirmDate = () => {
    setSelectedYear(tempYear);
    setSelectedMonth(Math.min(tempMonth, getMaxSelectableMonth(tempYear)));
    setIsDatePickerOpen(false);
  };

  const handleStatusClick = () => {
    setTempStatus(selectedStatus);
    setIsStatusPickerOpen(true);
  };

  const handleConfirmStatus = () => {
    setSelectedStatus(tempStatus);
    setIsStatusPickerOpen(false);
  };

  const handleExcelDownload = async () => {
    if (!hasData) return;

    const exportRows = await fetchPaymentDetailsAllPages();
    if (exportRows.length === 0) return;

    const filteredDetails = exportRows.filter((item) => {
      const matchesStatus = selectedStatus === 'all' || item.status === selectedStatus;
      const normalizedTerm = searchTerm.trim().toLocaleLowerCase();
      const matchesSearch = normalizedTerm.length === 0 || item.name.toLocaleLowerCase().includes(normalizedTerm);
      return matchesStatus && matchesSearch;
    });
    if (filteredDetails.length === 0) return;

    const dateExcelKey = (d: string) => (d?.trim() ? d : '0000.01.01');
    const sortedForExport = [...filteredDetails].sort((a, b) => {
      const c = dateExcelKey(b.date).localeCompare(dateExcelKey(a.date));
      if (c !== 0) return c;
      return a.name.localeCompare(b.name, 'ko');
    });

    const headerStyle = {
      fill: { fgColor: { rgb: 'D3D3D3' } },
      font: { bold: false, color: { rgb: '000000' } },
      alignment: { horizontal: 'center', vertical: 'center' },
      border: {
        top: { style: 'thin', color: { rgb: '000000' } },
        bottom: { style: 'thin', color: { rgb: '000000' } },
        left: { style: 'thin', color: { rgb: '000000' } },
        right: { style: 'thin', color: { rgb: '000000' } },
      },
    };

    const cellStyle = {
      alignment: { horizontal: 'center', vertical: 'center' },
      border: {
        top: { style: 'thin', color: { rgb: '000000' } },
        bottom: { style: 'thin', color: { rgb: '000000' } },
        left: { style: 'thin', color: { rgb: '000000' } },
        right: { style: 'thin', color: { rgb: '000000' } },
      },
    };

    const rightAlignStyle = {
      alignment: { horizontal: 'right', vertical: 'center' },
      border: {
        top: { style: 'thin', color: { rgb: '000000' } },
        bottom: { style: 'thin', color: { rgb: '000000' } },
        left: { style: 'thin', color: { rgb: '000000' } },
        right: { style: 'thin', color: { rgb: '000000' } },
      },
    };

    const descStyle = {
      alignment: { horizontal: 'left', vertical: 'center', wrapText: true },
      border: {
        top: { style: 'thin', color: { rgb: '000000' } },
        bottom: { style: 'thin', color: { rgb: '000000' } },
        left: { style: 'thin', color: { rgb: '000000' } },
        right: { style: 'thin', color: { rgb: '000000' } },
      },
    };

    const headers = [
      'No', 'Sponsor name', 'Donation Type', 'Phone', 'Payment Type',
      'Amount', 'Date', 'Status', 'Description',
    ];

    const data = sortedForExport.map((item, idx) => ([
      sortedForExport.length - idx,
      item.name,
      item.donationType || '',
      item.phone,
      t(`common.payment.method.${item.method}`),
      formatCurrency(item.amount),
      item.date,
      t(`common.payment.status.${item.status}`),
      item.paymentDescription || '',
    ]));

    const ws = XLSX.utils.aoa_to_sheet([headers, ...data]);
    ws['!cols'] = [
      { wch: 5 }, { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 15 },
      { wch: 15 }, { wch: 15 }, { wch: 10 }, { wch: 80 },
    ];

    if (ws['!ref']) {
      const range = XLSX.utils.decode_range(ws['!ref']);
      for (let c = range.s.c; c <= range.e.c; c += 1) {
        const address = XLSX.utils.encode_cell({ r: 0, c });
        if (ws[address]) ws[address].s = headerStyle;
      }
      for (let r = 1; r <= range.e.r; r += 1) {
        for (let c = range.s.c; c <= range.e.c; c += 1) {
          const address = XLSX.utils.encode_cell({ r, c });
          if (ws[address]) {
            ws[address].s = cellStyle;
            if (c === 5) ws[address].s = rightAlignStyle;
            if (c === 8) ws[address].s = descStyle;
          }
        }
      }
    }

    const wb = XLSX.utils.book_new();
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    const dateCompact = `${yyyy}${mm}${dd}`;
    const projectIdForFile = String(currentProject?.project_id ?? effectiveProjectId ?? 'project')
      .trim()
      .replace(/[^a-zA-Z0-9_-]/g, '_');
    XLSX.utils.book_append_sheet(wb, ws, `Payment_${dateCompact}`);
    const fileName = `payments_${projectIdForFile}_${dateCompact}.xlsx`;

    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([wbout], { type: 'application/octet-stream' }), fileName);
  };

  const handleSearch = () => {
    setSearchTerm((prev) => prev.trim());
  };

  if (shouldShowLoading) {
    return (
      <MobilePageLayout
        title={t('page.paymentStatus.pageTitle')}
        showBottomNav={false}
      >
        <DSLoading />
      </MobilePageLayout>
    );
  }

  const renderStatusPicker = () => (
    <DSBottomSheet
      open={isStatusPickerOpen}
      title={t('page.paymentStatus.filters.statusSelect')}
      onClose={() => setIsStatusPickerOpen(false)}
    >
      <StatusList>
        {statuses.map((status) => (
          <StatusItem
            key={status}
            onClick={() => setTempStatus(status)}
          >
            <RadioButton isSelected={tempStatus === status}>
              {tempStatus === status && <RadioInner />}
            </RadioButton>
            <StatusLabel isSelected={tempStatus === status}>{statusLabel(status)}</StatusLabel>
          </StatusItem>
        ))}
      </StatusList>
      <StatusConfirmButton type="button" onClick={handleConfirmStatus}>
        {t('common.actions.selectComplete')}
      </StatusConfirmButton>
    </DSBottomSheet>
  );

  const renderDatePicker = () => (
    <>
      <DimOverlay onClick={() => setIsDatePickerOpen(false)} />
      <DatePickerSheet>
        <PickerHeader>
          <PickerTitle>{t('page.paymentStatus.datePickerTitle')}</PickerTitle>
          <CloseButton onClick={() => setIsDatePickerOpen(false)} />
        </PickerHeader>
        
        <PickerContent>
          <PickerScrollArea>
            <YearColumn ref={yearRef} onScroll={handleYearScroll}>
              {selectableYears.map(year => (
                <PickerItem 
                  key={year} 
                  isSelected={tempYear === year}
                >
                  {yearLabel(year)}
                </PickerItem>
              ))}
            </YearColumn>
            <MonthColumn ref={monthRef} onScroll={handleMonthScroll}>
              {selectableMonthsForTempYear.map(month => (
                <PickerItem 
                  key={month} 
                  isSelected={tempMonth === month}
                >
                  {monthLabel(month)}
                </PickerItem>
              ))}
            </MonthColumn>
            <SelectedHighlightLeft />
            <SelectedHighlightRight />
          </PickerScrollArea>
        </PickerContent>

        <ConfirmButton onClick={handleConfirmDate}>
          {t('common.actions.selectComplete')}
        </ConfirmButton>
      </DatePickerSheet>
    </>
  );

  const renderSummary = () => (
    <>
      <SummaryList>
        {/* 약정 후원금액 */}
        <SummaryItem variant="yellow">
          <ItemLabel>{t('page.paymentStatus.summary.labels.promised')}</ItemLabel>
          <ItemValue>{formatSummaryCurrency(summary.promisedAmount)}</ItemValue>
          <ItemDescription>{t('page.paymentStatus.summary.descriptions.promised')}</ItemDescription>
        </SummaryItem>

        {/* 결제실패 금액 */}
        <SummaryItem variant="white">
          <ItemLabel>{t('page.paymentStatus.summary.labels.failed')}</ItemLabel>
          <ItemValue negative={true}>{formatSummaryCurrency(summary.failedAmount)}</ItemValue>
          <ItemDescription>
            {t('page.paymentStatus.summary.descriptions.failedLine1')} <br />
            {t('page.paymentStatus.summary.descriptions.failedLine2')}
          </ItemDescription>
        </SummaryItem>

        {/* 결제완료 금액 */}
        <SummaryItem variant="blue">
          <ItemLabel>{t('page.paymentStatus.summary.labels.completed')}</ItemLabel>
          <ItemValue highlight={true}>{formatSummaryCurrency(summary.completedAmount)}</ItemValue>
          <ItemDescription>{t('page.paymentStatus.summary.descriptions.completed')}</ItemDescription>
        </SummaryItem>

        {/* 결제 수수료 */}
        <SummaryItem variant="white">
          <ItemLabel>{t('page.paymentStatus.summary.labels.fees')}</ItemLabel>
          <ItemValue negative={true}>{formatSummaryCurrency(summary.fees)}</ItemValue>
          <ItemDescription>{t('page.paymentStatus.summary.descriptions.fees')}</ItemDescription>
        </SummaryItem>

        {/* 세계 선교기금 */}
        <SummaryItem variant="white">
          <ItemLabel>{t('page.paymentStatus.summary.labels.missionFund')}</ItemLabel>
          <ItemValue negative={true}>{formatSummaryCurrency(summary.missionFund)}</ItemValue>
          <ItemDescription>{t('page.paymentStatus.summary.descriptions.missionFund')}</ItemDescription>
        </SummaryItem>

        {/* 입금액 */}
        <FinalDepositBox>
          <FinalLabel>{t('page.paymentStatus.summary.labels.finalDeposit')}</FinalLabel>
          <FinalValue>{formatSummaryCurrency(summary.finalDeposit)}</FinalValue>
          <ItemDescription>{t('page.paymentStatus.summary.descriptions.finalDeposit')}</ItemDescription>
        </FinalDepositBox>
      </SummaryList>

      <ExcelButton onClick={handleExcelDownload}>
        <DownloadIcon />
        {t('common.actions.downloadExcel')}
      </ExcelButton>

      <NoticeSection>
        <NoticeTitle>{t('page.paymentStatus.notice.title')}</NoticeTitle>
        <NoticeList>
          <NoticeItem>{t('page.paymentStatus.notice.items.first')}</NoticeItem>
          <NoticeItem>{t('page.paymentStatus.notice.items.second')}</NoticeItem>
          <NoticeItem>{t('page.paymentStatus.notice.items.third')}</NoticeItem>
          <NoticeItem>{t('page.paymentStatus.notice.items.fourth')}</NoticeItem>
        </NoticeList>
      </NoticeSection>
    </>
  );

  const renderDetails = () => {
    const filteredDetails = details.filter((item) => {
      const matchesStatus = selectedStatus === 'all' || item.status === selectedStatus;
      const normalizedTerm = searchTerm.trim().toLocaleLowerCase();
      const matchesSearch = normalizedTerm.length === 0 || item.name.toLocaleLowerCase().includes(normalizedTerm);
      return matchesStatus && matchesSearch;
    });

    return (
      <>
        <FilterSection>
          <FilterInputWrapper>
            <FilterInput 
              placeholder={t('page.paymentStatus.filters.searchPlaceholder')} 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onBlur={handleSearch}
            />
            <SearchIconSmall />
          </FilterInputWrapper>
          <FilterInputWrapper onClick={handleStatusClick}>
            <StatusText isPlaceholder={selectedStatus === 'all'}>
              {selectedStatus === 'all' ? t('page.paymentStatus.filters.statusSelect') : statusLabel(selectedStatus)}
            </StatusText>
            <ArrowIcon />
          </FilterInputWrapper>
        </FilterSection>

        <DetailList>
          {filteredDetails.length > 0 ? (
            filteredDetails.map((item, index) => (
              <DetailItem key={item.id} isFirst={index === 0} isLast={index === filteredDetails.length - 1}>
                <ProfileSection>
                  <ProfileImg 
                    src={item.profileImg || defaultProfileIcon} 
                    alt={item.name} 
                    onError={(e) => { e.currentTarget.src = defaultProfileIcon; }}
                  />
                  <ProfileInfo>
                    <DonorName>{item.name}</DonorName>
                    <BadgeGroup>
                      <Badge type={item.method}>{t(`common.payment.method.${item.method}`)}</Badge>
                      <Badge type={item.status}>{t(`common.payment.status.${item.status}`)}</Badge>
                    </BadgeGroup>
                  </ProfileInfo>
                </ProfileSection>
                
                <DonorContactInfo>
                  {item.email?.trim() && (
                    <ContactItem>
                      <ContactEmailIcon />
                      {item.email}
                    </ContactItem>
                  )}
                  <ContactItem>
                    <ContactPhoneIcon />
                    {item.phone}
                  </ContactItem>
                  <ContactItem>
                    <ContactDateIcon />
                    {item.date}
                  </ContactItem>
                </DonorContactInfo>

                {item.message ? <DonorMessage>{item.message}</DonorMessage> : null}

                <AmountContainer>
                  <AmountBox>
                    <AmountLabel>{t('page.paymentStatus.details.amountLabel')}</AmountLabel>
                    <AmountValue color="main">{formatCurrency(item.amount)}</AmountValue>
                  </AmountBox>
                  <AmountBox>
                    <AmountLabel>{t('page.paymentStatus.details.feeLabel')}</AmountLabel>
                    <AmountValue color="sub">{formatCurrency(item.fee)}</AmountValue>
                  </AmountBox>
                </AmountContainer>
              </DetailItem>
            ))
          ) : (
            <NoResultsMobile>
              <NoResultsTextMobile>{t('page.paymentStatus.details.noResults')}</NoResultsTextMobile>
              <NoResultsIconMobile />
            </NoResultsMobile>
          )}
        </DetailList>
      </>
    );
  };

  return (
    <MobilePageLayout 
      title={t('page.paymentStatus.pageTitle')} 
      hideFooter={isDatePickerOpen || isStatusPickerOpen}
      showBottomNav={!isDatePickerOpen && !isStatusPickerOpen}
    >
      <Container>
        <ProjectDetailHeader title={projectTitle} projectId={currentProject?.project_id} variant="mobile" />
          <PageTitle>{t('page.paymentStatus.pageTitle')}</PageTitle>

        {!hasData ? (
          showPreparingEmptyState ? (
            <>
              <DatePickerSection>
                <DateArrow direction="left" onClick={handlePrevMonth} disabled={isPrevDisabled} />
                <DateText onClick={handleDateClick}>
                  {yearLabel(selectedYear)} {monthLabel(selectedMonth)}
                  <ArrowUpIcon />
                </DateText>
                <DateArrow direction="right" onClick={handleNextMonth} disabled={isNextDisabled} />
              </DatePickerSection>

              <TabWrapper>
                <Tab active={activeTab === 'summary'} onClick={() => setActiveTab('summary')}>
                  <TabInner>
                    <SummaryTabIcon active={activeTab === 'summary'} />
                    {t('page.paymentStatus.tabs.summary')}
                  </TabInner>
                </Tab>
                <Tab active={activeTab === 'details'} onClick={() => setActiveTab('details')}>
                  <TabInner>
                    <DetailsTabIcon active={activeTab === 'details'} />
                    {t('page.paymentStatus.tabs.details')}
                  </TabInner>
                </Tab>
              </TabWrapper>

              <MainContent>
                <PreparingStateWrapper>
                  <EmptyClockIcon xmlns="http://www.w3.org/2000/svg" viewBox="0 0 160 160" fill="none">
                    <path d="M80 140C71.6667 140 63.8622 138.418 56.5867 135.253C49.3111 132.089 42.9778 127.811 37.5867 122.42C32.1956 117.029 27.9178 110.696 24.7533 103.42C21.5889 96.1444 20.0045 88.3378 20 80C19.9956 71.6622 21.58 63.8578 24.7533 56.5867C27.9267 49.3156 32.2022 42.9822 37.58 37.5867C42.9578 32.1911 49.2911 27.9133 56.58 24.7533C63.8689 21.5933 71.6756 20.0089 80 20C89.1111 20 97.7511 21.9444 105.92 25.8333C114.089 29.7222 121.004 35.2222 126.667 42.3333V33.3333C126.667 31.4444 127.307 29.8622 128.587 28.5867C129.867 27.3111 131.449 26.6711 133.333 26.6667C135.218 26.6622 136.802 27.3022 138.087 28.5867C139.371 29.8711 140.009 31.4533 140 33.3333V60C140 61.8889 139.36 63.4733 138.08 64.7533C136.8 66.0333 135.218 66.6711 133.333 66.6667H106.667C104.778 66.6667 103.196 66.0267 101.92 64.7467C100.644 63.4667 100.004 61.8844 100 60C99.9956 58.1156 100.636 56.5333 101.92 55.2533C103.204 53.9733 104.787 53.3333 106.667 53.3333H118.333C113.778 47.1111 108.167 42.2222 101.5 38.6667C94.8333 35.1111 87.6667 33.3333 80 33.3333C67 33.3333 55.9733 37.8622 46.92 46.92C37.8667 55.9778 33.3378 67.0044 33.3333 80C33.3289 92.9956 37.8578 104.024 46.92 113.087C55.9822 122.149 67.0089 126.676 80 126.667C90.5556 126.667 100 123.5 108.333 117.167C116.667 110.833 122.167 102.667 124.833 92.6667C125.389 90.8889 126.389 89.5556 127.833 88.6667C129.278 87.7778 130.889 87.4444 132.667 87.6667C134.556 87.8889 136.056 88.6933 137.167 90.08C138.278 91.4667 138.611 92.9956 138.167 94.6667C134.944 107.889 127.944 118.751 117.167 127.253C106.389 135.756 94 140.004 80 140ZM86.6667 77.3333L103.333 94C104.556 95.2222 105.167 96.7778 105.167 98.6667C105.167 100.556 104.556 102.111 103.333 103.333C102.111 104.556 100.556 105.167 98.6667 105.167C96.7778 105.167 95.2222 104.556 94 103.333L75.3333 84.6667C74.6667 84 74.1667 83.2511 73.8333 82.42C73.5 81.5889 73.3333 80.7267 73.3333 79.8333V53.3333C73.3333 51.4444 73.9733 49.8622 75.2533 48.5867C76.5333 47.3111 78.1156 46.6711 80 46.6667C81.8844 46.6622 83.4689 47.3022 84.7533 48.5867C86.0378 49.8711 86.6756 51.4533 86.6667 53.3333V77.3333Z" fill="#DEF9FF"/>
                  </EmptyClockIcon>
                  <PreparingTitleBlue>데이터를 준비 중입니다</PreparingTitleBlue>
                  <PreparingTitleBlack>
                    <NoDataLine>현재 정산 시스템 점검 또는 집계 지연으로</NoDataLine>
                    <NoDataLine>해당 월의 결제 현황을 표시할 수 없습니다.</NoDataLine>
                  </PreparingTitleBlack>
                </PreparingStateWrapper>

                <NoticeSection>
                  <NoticeTitle>{t('page.paymentStatus.notice.title')}</NoticeTitle>
                  <NoticeList>
                    <NoticeItem>{t('page.paymentStatus.notice.items.first')}</NoticeItem>
                    <NoticeItem>{t('page.paymentStatus.notice.items.second')}</NoticeItem>
                    <NoticeItem>{t('page.paymentStatus.notice.items.third')}</NoticeItem>
                    <NoticeItem>{t('page.paymentStatus.notice.items.fourth')}</NoticeItem>
                  </NoticeList>
                </NoticeSection>
              </MainContent>
            </>
          ) : (
            <MainContent>
              <EmptyStateWrapper>
                <EmptyTitleGroup>
                  <EmptyTitleBlue>
                    {t('common.empty.titleBlueLine1')} <br /> {t('common.empty.titleBlueLine2')}
                  </EmptyTitleBlue>
                  <EmptyTitleBlack>{t('common.empty.titleBlackLine1')} <br /> {t('common.empty.titleBlackLine2')}</EmptyTitleBlack>
                </EmptyTitleGroup>
                <ShareBtn onClick={() => setIsShareBottomSheetOpen(true)}>
                  <ShareIcon src="/Image/posts/shareIcon.png" alt={t('common.empty.shareAlt')} />
                  {t('common.empty.share')}
                </ShareBtn>
              </EmptyStateWrapper>
            </MainContent>
          )
        ) : (
          <>
            <DatePickerSection>
              <DateArrow direction="left" onClick={handlePrevMonth} disabled={isPrevDisabled} />
              <DateText onClick={handleDateClick}>
                {yearLabel(selectedYear)} {monthLabel(selectedMonth)}
                <ArrowUpIcon />
              </DateText>
              <DateArrow direction="right" onClick={handleNextMonth} disabled={isNextDisabled} />
            </DatePickerSection>

            <TabWrapper>
              <Tab active={activeTab === 'summary'} onClick={() => setActiveTab('summary')}>
                <TabInner>
                  <SummaryTabIcon active={activeTab === 'summary'} />
                  {t('page.paymentStatus.tabs.summary')}
                </TabInner>
              </Tab>
              <Tab active={activeTab === 'details'} onClick={() => setActiveTab('details')}>
                <TabInner>
                  <DetailsTabIcon active={activeTab === 'details'} />
                  {t('page.paymentStatus.tabs.details')}
                </TabInner>
              </Tab>
            </TabWrapper>

            <MainContent>
              {activeTab === 'summary' ? renderSummary() : renderDetails()}
            </MainContent>
          </>
        )}
      </Container>
      <DSBottomSheet open={isShareBottomSheetOpen} onClose={handleCloseShareBottomSheet} padding="0">
        <ShareBottomSheetWrapper>
          <ShareHeader>
            <TitleRow>
              <ShareTitle>{t('page.donorManagement.shareModal.title')}</ShareTitle>
              <ShareCloseButton onClick={handleCloseShareBottomSheet} aria-label="close">
                ✕
              </ShareCloseButton>
            </TitleRow>
            <ShareDescription>
              {t('common.shareModal.descLine1')}
              <br />
              {t('common.shareModal.descLine2')}
            </ShareDescription>
          </ShareHeader>

          <ShareOptionsContainer>
            <ShareOption onClick={handleKakaoShareOption}>
              <KakaoIconBox>
                <KakaoIconImg src={kakaoIcon} alt={t('common.shareModal.kakaoAlt')} />
              </KakaoIconBox>
              <ShareOptionText>{t('common.shareModal.kakaoOption')}</ShareOptionText>
            </ShareOption>

            <ShareOption onClick={handleEmailShareOption}>
              <ShareEmailIconBox>
                <EmailIconLarge src={emailIcon} alt={t('common.shareModal.emailAlt')} />
              </ShareEmailIconBox>
              <ShareOptionText>{t('common.shareModal.emailOption')}</ShareOptionText>
            </ShareOption>
          </ShareOptionsContainer>

          <CopyLinkButtonContainer>
            <CopyLinkButton onClick={handleCopyLink}>
              <CopyIcon />
              {t('common.shareModal.copyLink')}
            </CopyLinkButton>
          </CopyLinkButtonContainer>
        </ShareBottomSheetWrapper>
      </DSBottomSheet>
      {isDatePickerOpen && renderDatePicker()}
      {isStatusPickerOpen && renderStatusPicker()}
    </MobilePageLayout>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const PageTitle = styled.h1`
  font-size: ${convertToVwMob(24)};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.grayScale[90]};
`;

const TabWrapper = styled.div`
  display: flex;
  border-bottom: 1px solid ${({ theme }) => theme.colors.grayScale[20]};
  background: white;
  padding: 0 ${convertToVwMob(20)};
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
  border: 1px solid ${({ theme }) => theme.colors.grayScale[20]};
`;

const Tab = styled.button<{ active: boolean }>`
  flex: 1;
  background: none;
  border: none;
  padding: 16px 0;
  padding-top: 20px;
  font-size: ${convertToVwMob(20)};
  font-weight: 500;
  color: ${({ theme, active }) => (active ? theme.colors.primaryBlue[40] : theme.colors.grayScale[50])};
  border-bottom: ${convertToVwMob(4)} solid ${({ theme, active }) => (active ? theme.colors.primaryBlue[40] : 'transparent')};
  cursor: pointer;
`;

const TabInner = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${convertToVwMob(8)};
`;

const SummaryTabIcon = ({ active }: { active: boolean }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={convertToVwMob(21)} height={convertToVwMob(21)} viewBox="0 0 21 21" fill="none" aria-hidden focusable="false">
    <path fillRule="evenodd" clipRule="evenodd" d="M15.7491 8.99911C15.7491 9.88554 15.5745 10.7633 15.2353 11.5822C14.8961 12.4012 14.3989 13.1453 13.7721 13.7721C13.1453 14.3989 12.4012 14.8961 11.5822 15.2353C10.7633 15.5745 9.88554 15.7491 8.99911 15.7491C8.11269 15.7491 7.23495 15.5745 6.416 15.2353C5.59705 14.8961 4.85294 14.3989 4.22614 13.7721C3.59935 13.1453 3.10215 12.4012 2.76293 11.5822C2.42371 10.7633 2.24911 9.88554 2.24911 8.99911C2.24911 7.2089 2.96027 5.49201 4.22614 4.22614C5.49201 2.96027 7.2089 2.24911 8.99911 2.24911C10.7893 2.24911 12.5062 2.96027 13.7721 4.22614C15.038 5.49201 15.7491 7.2089 15.7491 8.99911ZM14.5191 16.1091C12.7107 17.5131 10.4352 18.1751 8.15581 17.9603C5.87646 17.7456 3.76463 16.6702 2.25024 14.9532C0.735845 13.2362 -0.0672602 11.0066 0.00441596 8.71824C0.0760921 6.42992 1.01716 4.25492 2.63604 2.63604C4.25492 1.01716 6.42992 0.0760921 8.71824 0.00441596C11.0066 -0.0672602 13.2362 0.735845 14.9532 2.25024C16.6702 3.76463 17.7456 5.87646 17.9603 8.15581C18.1751 10.4352 17.5131 12.7107 16.1091 14.5191L20.2941 18.7041C20.4046 18.8071 20.4933 18.9313 20.5548 19.0693C20.6163 19.2073 20.6493 19.3563 20.652 19.5073C20.6547 19.6584 20.6269 19.8084 20.5703 19.9485C20.5137 20.0886 20.4295 20.2158 20.3227 20.3227C20.2158 20.4295 20.0886 20.5137 19.9485 20.5703C19.8084 20.6269 19.6584 20.6547 19.5073 20.652C19.3563 20.6493 19.2073 20.6163 19.0693 20.5548C18.9313 20.4933 18.8071 20.4046 18.7041 20.2941L14.5191 16.1091Z" fill={active ? "#2BC2E1" : "#BCBCBC"} />
  </svg>
);

const DetailsTabIcon = ({ active }: { active: boolean }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={convertToVwMob(24)} height={convertToVwMob(24)} viewBox="0 0 24 24" fill="none" aria-hidden focusable="false">
    <path d="M8 13.7998H16C17.1139 13.7998 18.1821 14.2426 18.9697 15.0303C19.7574 15.8179 20.2002 16.8861 20.2002 18C20.2002 18.5835 19.9682 19.1431 19.5557 19.5557C19.1431 19.9682 18.5835 20.2002 18 20.2002H6C5.41652 20.2002 4.85692 19.9682 4.44434 19.5557C4.03176 19.1431 3.7998 18.5835 3.7998 18L3.80469 17.792C3.85612 16.7538 4.29178 15.7688 5.03027 15.0303C5.81793 14.2426 6.88609 13.7998 8 13.7998ZM12 3.7998C12.8487 3.7998 13.6626 4.13719 14.2627 4.7373C14.8628 5.33742 15.2002 6.15131 15.2002 7C15.2002 7.84869 14.8628 8.66258 14.2627 9.2627C13.6626 9.86281 12.8487 10.2002 12 10.2002C11.1513 10.2002 10.3374 9.86281 9.7373 9.2627C9.13719 8.66258 8.7998 7.84869 8.7998 7C8.7998 6.15131 9.13719 5.33742 9.7373 4.7373C10.3374 4.13719 11.1513 3.7998 12 3.7998Z" stroke={active ? "#2BC2E1" : "#BCBCBC"} strokeWidth="1.6" />
  </svg>
);

const MainContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${convertToVwMob(20)};
  margin-top: ${convertToVwMob(20)};
`;

const DatePickerSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${convertToVwMob(16)};
  padding: ${convertToVwMob(16)} 0;
  margin: 0 ${convertToVwMob(20)};
`;

const DateText = styled.span`
  font-size: ${convertToVwMob(24)};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.grayScale[90]};
`;

const DateArrow = styled.button<{ direction: 'left' | 'right' }>`
  width: ${convertToVwMob(20)};
  height: ${convertToVwMob(40)};
  background: none;
  border: none;
  background-image: url("data:image/svg+xml,%3Csvg width='20' height='40' viewBox='0 0 20 40' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M13 15L8 20L13 25' stroke='%2317171B' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  transform: ${({ direction }) => (direction === 'right' ? 'rotate(180deg)' : 'none')};
  cursor: pointer;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const SummaryList = styled.div`
  display: flex;
  flex-direction: column;
`;

const SummaryItem = styled.div<{ variant: 'yellow' | 'white' | 'blue' }>`
  padding: ${convertToVwMob(16)} ${convertToVwMob(20)};
  background: ${({ theme, variant }) => {
    if (variant === 'yellow') return theme.colors.yellow[5];
    if (variant === 'blue') return theme.colors.primaryBlue[10];
    return 'white';
  }};
  border-bottom: 1px solid ${({ theme }) => theme.colors.grayScale[20]};
  display: flex;
  flex-direction: column;
  gap: ${convertToVwMob(4)};
`;

const ItemLabel = styled.span`
  font-size: ${convertToVwMob(20)};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.grayScale[70]};
`;

const ItemValue = styled.span<{ negative?: boolean; highlight?: boolean }>`
  font-size: ${convertToVwMob(28)};
  font-weight: 700;
  color: ${({ theme, negative, highlight }) => {
    if (highlight) return theme.colors.primaryBlue[40];
    if (negative) return theme.colors.grayScale[90];
    return theme.colors.grayScale[70];
  }};
`;

const ItemDescription = styled.p`
  font-size: ${convertToVwMob(16)};
  color: ${({ theme }) => theme.colors.grayScale[60]};
  margin: 0;
  line-height: 1.5;
`;

const FinalDepositBox = styled.div`
  padding: ${convertToVwMob(16)} ${convertToVwMob(20)};
  background: ${({ theme }) => theme.colors.primaryBlue[10]};
  border: 2px solid ${({ theme }) => theme.colors.primaryBlue[40]};
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  gap: ${convertToVwMob(4)};
  margin-top: ${convertToVwMob(18)};
`;

const FinalLabel = styled.span`
  font-size: ${convertToVwMob(20)};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.grayScale[90]};
`;

const FinalValue = styled.span`
  font-size: ${convertToVwMob(28)};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.primaryBlue[40]};
`;

const ExcelButton = styled.button`
  width: 100%;
  height: ${convertToVwMob(58)};
  background: ${({ theme }) => theme.colors.green[65]};
  color: white;
  border: none;
  border-radius: 100px;
  font-size: ${convertToVwMob(20)};
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${convertToVwMob(8)};
  cursor: pointer;
`;

const DownloadIcon = styled.div`
  width: ${convertToVwMob(24)};
  height: ${convertToVwMob(24)};
  background-image: url("data:image/svg+xml,%3Csvg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M12 16L7 11L8.4 9.55L11 12.15V4H13V12.15L15.6 9.55L17 11L12 16ZM6 20C5.45 20 4.97917 19.8042 4.5875 19.4125C4.19583 19.0208 4 18.55 4 18V15H6V18H18V15H20V18C20 18.55 19.8042 19.0208 19.4125 19.4125C19.0208 19.8042 18.55 20 18 20H6Z' fill='white'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
`;

const NoticeSection = styled.div`
  background: white;
  border: 1px solid ${({ theme }) => theme.colors.grayScale[20]};
  border-radius: 10px;
  padding: ${convertToVwMob(30)};
  display: flex;
  flex-direction: column;
  gap: ${convertToVwMob(10)};
  margin-bottom: ${convertToVwMob(40)};
`;

const NoticeTitle = styled.h3`
  font-size: ${convertToVwMob(20)};
  font-weight: 500;
  color: ${({ theme }) => theme.colors.grayScale[90]};
  margin: 0;
`;

const NoticeList = styled.ul`
  margin: 0;
  padding: 0;
  list-style-type: none;
`;

const NoticeItem = styled.li`
  font-size: ${convertToVwMob(16)};
  color: ${({ theme }) => theme.colors.grayScale[90]};
  line-height: 1.5;
  display: flex;
  gap: ${convertToVwMob(8)};
  
  &::before {
    content: '•';
  }
`;

const PreparingStateWrapper = styled.div`
  background: white;
  border: 1px solid ${({ theme }) => theme.colors.grayScale[20]};
  border-radius: ${convertToVwMob(12)};
  min-height: ${convertToVwMob(420)};
  padding: ${convertToVwMob(56)} ${convertToVwMob(20)};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${convertToVwMob(20)};
  width: 100%;
  box-sizing: border-box;
`;

const PreparingTitleBlue = styled.p`
  font-family: Pretendard;
  font-size: ${convertToVwMob(24)};
  font-style: normal;
  font-weight: 600;
  line-height: 120%;
  color: ${({ theme }) => theme.colors.primaryBlue[40]};
  margin: 0;
  text-align: center;
`;

const PreparingTitleBlack = styled.p`
  font-family: Pretendard;
  font-size: clamp(14px, ${convertToVwMob(20)}, 20px);
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
  color: ${({ theme }) => theme.colors.grayScale[50]};
  margin: 0;
  text-align: center;
`;

const EmptyStateWrapper = styled.div`
  background: white;
  border: 1px solid ${({ theme }) => theme.colors.grayScale[20]};
  border-radius: 20px;
  padding: ${convertToVwMob(24)} ${convertToVwMob(20)};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${convertToVwMob(29)};
  width: 100%;
  box-sizing: border-box;
`;

const EmptyTitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
  width: 100%;
  gap: 0;
`;

const EmptyTitleBlue = styled.p`
  font-size: ${convertToVwMob(20)};
  color: ${({ theme }) => theme.colors.primaryBlue[40]};
  margin: 0;
  line-height: 1.5;
`;

const EmptyTitleBlack = styled.p`
  font-size: ${convertToVwMob(20)};
  color: ${({ theme }) => theme.colors.grayScale[90]};
  margin: 0;
  line-height: 1.5;
`;

const NoDataLine = styled.span`
  display: block;
  white-space: nowrap;
  word-break: keep-all;
  line-break: strict;
`;

const EmptyClockIcon = styled.svg`
  width: ${convertToVwMob(160)};
  height: ${convertToVwMob(160)};
`;

const ShareBtn = styled.button`
  background: #026478;
  border: none;
  border-radius: 50px;
  width: 100%;
  padding: ${convertToVwMob(14)} ${convertToVwMob(24)};
  min-width: ${convertToVwMob(240)};
  height: ${convertToVwMob(58)};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${convertToVwMob(8)};
  box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.25);
  cursor: pointer;
  font-size: ${convertToVwMob(20)};
  font-weight: 700;
  color: white;
  white-space: nowrap;
`;

const ShareIcon = styled.img`
  width: ${convertToVwMob(30)};
  height: ${convertToVwMob(30)};
  object-fit: contain;
  aspect-ratio: 1 / 1;
`;

const ShareBottomSheetWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding-top: ${convertToVwMob(20)};
`;

const ShareHeader = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 ${convertToVwMob(24)};
  margin-bottom: ${convertToVwMob(24)};
`;

const TitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${convertToVwMob(12)};
`;

const ShareTitle = styled.h2`
  color: ${({ theme }) => theme.colors.grayScale[90]};
  ${({ theme }) => theme.typography.head.semiBold24}
  margin: 0;
  text-align: left;
`;

const ShareCloseButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.grayScale[60]};
  font-size: ${convertToVwMob(36)};
  font-weight: 300;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${convertToVwMob(44)};
  height: ${convertToVwMob(44)};
  line-height: 1;
  cursor: pointer;
`;

const ShareDescription = styled.p`
  color: ${({ theme }) => theme.colors.grayScale[70]};
  ${({ theme }) => theme.typography.contents.regular20}
  margin: 0;
  padding: 0;
  text-align: left;
`;

const ShareOptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${convertToVwMob(16)};
  margin-bottom: ${convertToVwMob(20)};
  padding: 0 ${convertToVwMob(24)};
`;

const ShareOption = styled.button`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${convertToVwMob(20)};
  background: ${({ theme }) => theme.colors.grayScale[0]};
  width: 100%;
  border: none;
  outline: none;
  cursor: pointer;
  padding: 0;
`;

const KakaoIconBox = styled.div`
  width: ${convertToVwMob(60)};
  height: ${convertToVwMob(60)};
  background: #f9e000;
  border-radius: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const KakaoIconImg = styled.img`
  width: ${convertToVwMob(34)};
  height: ${convertToVwMob(34)};
`;

const ShareEmailIconBox = styled.div`
  width: ${convertToVwMob(60)};
  height: ${convertToVwMob(60)};
  background: #EBFCFF;
  border-radius: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const EmailIconLarge = styled.img`
  width: ${convertToVwMob(34)};
  height: ${convertToVwMob(34)};
  object-fit: contain;
`;

const ShareOptionText = styled.span`
  ${({ theme }) => theme.typography.contents.medium20}
  color: ${({ theme }) => theme.colors.grayScale[90]};
  text-align: left;
  flex: 1;
`;

const CopyLinkButtonContainer = styled.div`
  padding: ${convertToVwMob(20)} ${convertToVwMob(30)};
  background: #F8F8F8;
`;

const CopyLinkButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${convertToVwMob(8)};
  width: 100%;
  padding: ${convertToVwMob(12)} ${convertToVwMob(16)};
  background: ${({ theme }) => theme.colors.grayScale[0]};
  border: 1px solid #929292;
  border-radius: ${convertToVwMob(10)};
  color: ${({ theme }) => theme.colors.grayScale[90]};
  ${({ theme }) => theme.typography.contents.medium20}
  cursor: pointer;
`;

const CopyIcon = styled.div`
  width: ${convertToVwMob(20)};
  height: ${convertToVwMob(20)};
  background-image: url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M16 4H4V16H16V4ZM16 2C17.1 2 18 2.9 18 4V16C18 17.1 17.1 18 16 18H4C2.9 18 2 17.1 2 16V4C2 2.9 2.9 2 4 2H16ZM20 8V20H8V22H20C21.1 22 22 21.1 22 20V8H20Z' fill='%2317171B'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
`;

// 결제 상세 내역 관련 스타일
const FilterSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${convertToVwMob(12)};
`;

const FilterInputWrapper = styled.div`
  box-sizing: border-box;
  min-width: 0;
  height: ${convertToVwMob(52)};
  border: 1px solid ${({ theme }) => theme.colors.grayScale[20]};
  border-radius: 4px;
  background: white;
  display: flex;
  align-items: center;
  padding: 0 ${convertToVwMob(12)};
  gap: ${convertToVwMob(8)};
  justify-content: flex-start;
`;

const FilterInput = styled.input`
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  padding: 0;
  margin: 0;
  background: transparent;
  appearance: none;
  -webkit-appearance: none;
  font-family: Pretendard;
  font-size: ${convertToVwMob(24)};
  font-weight: 400;
  line-height: 150%;
  color: ${({ theme }) => theme.colors.grayScale[90]};
  &::placeholder {
    font-family: Pretendard;
    color: ${({ theme }) => theme.colors.grayScale[60]};
  }
`;

const StatusText = styled.span<{ isPlaceholder: boolean }>`
  flex: 1;
  font-family: Pretendard;
  font-size: ${convertToVwMob(24)};
  font-weight: 400;
  line-height: 150%;
  color: ${({ theme, isPlaceholder }) => (isPlaceholder ? theme.colors.grayScale[60] : theme.colors.grayScale[90])};
`;

const SearchIconSmall = styled.div`
  flex-shrink: 0;
  width: ${convertToVwMob(20)};
  height: ${convertToVwMob(20)};
  background-image: url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 19C15.4183 19 19 15.4183 19 11C19 6.58172 15.4183 3 11 3C6.58172 3 3 6.58172 3 11C3 15.4183 6.58172 19 11 19Z' stroke='%23929292' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3Cpath d='M21 21L16.65 16.65' stroke='%23929292' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
`;

const ArrowIcon = styled.div`
  flex-shrink: 0;
  width: ${convertToVwMob(24)};
  height: ${convertToVwMob(24)};
  background-image: url("data:image/svg+xml,%3Csvg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M7 10L12 15L17 10' stroke='%23929292' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
`;

const ArrowUpIcon = styled.div`
  width: ${convertToVwMob(12)};
  height: ${convertToVwMob(12)};
  background-image: url("data:image/svg+xml,%3Csvg width='12' height='12' viewBox='0 0 12 12' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M2 8L6 4L10 8' stroke='%2317171B' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  display: inline-block;
  margin-left: ${convertToVwMob(8)};
  transform: rotate(180deg);
`;

// 날짜 선택 팝업 관련 스타일
const DimOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1100;
`;

const DatePickerSheet = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  background: white;
  border-top-left-radius: 30px;
  border-top-right-radius: 30px;
  padding: ${convertToVwMob(30)} ${convertToVwMob(20)} ${convertToVwMob(40)};
  z-index: 1101;
  display: flex;
  flex-direction: column;
  gap: ${convertToVwMob(24)};
`;

const PickerHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const PickerTitle = styled.h2`
  font-size: ${convertToVwMob(24)};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.grayScale[90]};
  margin: 0;
`;

const CloseButton = styled.button`
  width: ${convertToVwMob(20)};
  height: ${convertToVwMob(20)};
  background: none;
  border: none;
  background-image: url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 20 20' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M15 5L5 15M5 5L15 15' stroke='%2317171B' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  cursor: pointer;
`;

const PickerContent = styled.div`
  height: ${convertToVwMob(242)};
  position: relative;
  overflow: hidden;
`;

const PickerScrollArea = styled.div`
  display: flex;
  height: 100%;
  gap: ${convertToVwMob(20)};
  position: relative;
`;

const YearColumn = styled.div`
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${convertToVwMob(98)} 0;
  scroll-snap-type: y mandatory;
  
  &::-webkit-scrollbar {
    display: none;
  }
`;

const MonthColumn = styled.div`
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${convertToVwMob(98)} 0;
  scroll-snap-type: y mandatory;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const StatusList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${convertToVwMob(12)};
  margin-bottom: ${convertToVwMob(16)};
`;

const StatusItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${convertToVwMob(10)};
  cursor: pointer;
  padding: ${convertToVwMob(6)} 0;
`;

const RadioButton = styled.div<{ isSelected: boolean }>`
  width: ${convertToVwMob(24)};
  height: ${convertToVwMob(24)};
  border-radius: 50%;
  border: 2px solid ${({ theme, isSelected }) => (isSelected ? theme.colors.primaryBlue[40] : '#bcbcbc')};
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
`;

const RadioInner = styled.div`
  width: ${convertToVwMob(12)};
  height: ${convertToVwMob(12)};
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.primaryBlue[40]};
`;

const StatusLabel = styled.span<{ isSelected: boolean }>`
  font-size: ${convertToVwMob(20)};
  color: ${({ isSelected }) => (isSelected ? '#17171B' : '#929292')};
  font-weight: ${({ isSelected }) => (isSelected ? 500 : 400)};
  line-height: 1.5;
`;

const StatusConfirmButton = styled.button`
  width: 100%;
  height: ${convertToVwMob(60)};
  background: #026478;
  color: white;
  border: none;
  border-radius: ${convertToVwMob(12)};
  font-size: ${convertToVwMob(18)};
  font-weight: 500;
  cursor: pointer;
`;

const PickerItem = styled.div<{ isSelected: boolean }>`
  height: ${convertToVwMob(46)};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${convertToVwMob(24)};
  color: ${({ isSelected, theme }) => (isSelected ? theme.colors.grayScale[90] : theme.colors.grayScale[40])};
  cursor: pointer;
  width: 100%;
  scroll-snap-align: center;
  flex-shrink: 0;
`;

const SelectedHighlightLeft = styled.div`
  position: absolute;
  top: ${convertToVwMob(98)};
  left: 0;
  width: calc(50% - ${convertToVwMob(10)});
  height: ${convertToVwMob(46)};
  background: ${({ theme }) => theme.colors.grayScale[20]};
  border-radius: 12px;
  z-index: -1;
`;

const SelectedHighlightRight = styled.div`
  position: absolute;
  top: ${convertToVwMob(98)};
  right: 0;
  width: calc(50% - ${convertToVwMob(10)});
  height: ${convertToVwMob(46)};
  background: ${({ theme }) => theme.colors.grayScale[20]};
  border-radius: 12px;
  z-index: -1;
`;

const ConfirmButton = styled.button`
  width: 100%;
  height: ${convertToVwMob(62)};
  background: ${({ theme }) => theme.colors.primaryBlue[80]};
  color: white;
  border: none;
  border-radius: 12px;
  font-size: ${convertToVwMob(20)};
  font-weight: 500;
  cursor: pointer;
`;

const DetailList = styled.div`
  display: flex;
  flex-direction: column;
  background: white;
  border: 1px solid ${({ theme }) => theme.colors.grayScale[20]};
  border-radius: 12px;
  overflow: hidden;
`;

const NoResultsMobile = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${convertToVwMob(19)};
  height: ${convertToVwMob(220)};
  background: white;
  border-radius: 12px;
  width: 100%;
`;

const NoResultsTextMobile = styled.p`
  font-size: ${convertToVwMob(20)};
  color: ${({ theme }) => theme.colors.grayScale[50]};
  margin: 0;
`;

const NoResultsIconMobile = styled.div`
  width: ${convertToVwMob(50)};
  height: ${convertToVwMob(50)};
  background-image: url("data:image/svg+xml,%3Csvg width='50' height='50' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M12 12M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z' stroke='%23bcbcbc' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3Cpath d='M9 10L9.01 10' stroke='%23bcbcbc' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3Cpath d='M15 10L15.01 10' stroke='%23bcbcbc' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3Cpath d='M9 15H15' stroke='%23bcbcbc' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
`;

const DetailItem = styled.div<{ isFirst: boolean; isLast: boolean }>`
  padding: ${convertToVwMob(20)};
  border-bottom: ${({ isLast }) => (isLast ? 'none' : `1px solid #eee`)};
  display: flex;
  flex-direction: column;
  gap: ${convertToVwMob(16)};
`;

const ProfileSection = styled.div`
  display: flex;
  gap: ${convertToVwMob(16)};
  align-items: center;
`;

const ProfileImg = styled.img`
  width: ${convertToVwMob(84)};
  height: ${convertToVwMob(84)};
  border-radius: 50%;
  object-fit: cover;
`;

const ProfileInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${convertToVwMob(4)};
`;

const DonorName = styled.span`
  font-size: ${convertToVwMob(20)};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.grayScale[90]};
`;

const BadgeGroup = styled.div`
  display: flex;
  gap: ${convertToVwMob(4)};
`;

const Badge = styled.span<{ type: 'cms' | 'card' | 'complete' | 'fail' }>`
  padding: ${convertToVwMob(4)} ${convertToVwMob(10)};
  border-radius: 4px;
  font-size: ${convertToVwMob(12)};
  ${({ theme, type }) => {
    switch (type) {
      case 'cms':
        return `background: ${theme.colors.primaryBlue[80]}; color: white;`;
      case 'card':
        return `background: #ebfcff; color: #25b9d8;`;
      case 'complete':
        return `background: #b7f2ff; color: ${theme.colors.grayScale[90]};`;
      case 'fail':
        return `background: white; border: 1px solid #ffc0c0; color: #ff0000;`;
    }
  }}
`;

const DonorContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${convertToVwMob(8)};
`;

const ContactItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${convertToVwMob(8)};
  font-size: ${convertToVwMob(16)};
  color: ${({ theme }) => theme.colors.grayScale[70]};
`;

const ContactEmailIcon = styled.div`
  width: ${convertToVwMob(16)};
  height: ${convertToVwMob(16)};
  background-image: url("data:image/svg+xml,%3Csvg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M13.3333 2.66666H2.66666C1.93333 2.66666 1.33333 3.26666 1.33333 3.99999V12C1.33333 12.7333 1.93333 13.3333 2.66666 13.3333H13.3333C14.0667 13.3333 14.6667 12.7333 14.6667 12V3.99999C14.6667 3.26666 14.0667 2.66666 13.3333 2.66666ZM13.3333 5.33332L8 8.66666L2.66666 5.33332V3.99999L8 7.33332L13.3333 3.99999V5.33332Z' fill='%23676767'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  flex-shrink: 0;
`;

const ContactPhoneIcon = styled.div`
  width: ${convertToVwMob(16)};
  height: ${convertToVwMob(16)};
  background-image: url("data:image/svg+xml,%3Csvg width='16' height='16' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M6.62 10.79C8.06 13.62 10.38 15.94 13.21 17.38L15.41 15.18C15.68 14.91 16.08 14.82 16.43 14.94C17.55 15.31 18.76 15.51 20 15.51C20.55 15.51 21 15.96 21 16.51V20C21 20.55 20.55 21 20 21C10.61 21 3 13.39 3 4C3 3.45 3.45 3 4 3H7.5C8.05 3 8.5 3.45 8.5 4C8.5 5.24 8.7 6.45 9.07 7.57C9.18 7.92 9.1 8.31 8.82 8.59L6.62 10.79Z' fill='%23676767'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  flex-shrink: 0;
`;

const ContactDateIcon = styled.div`
  width: ${convertToVwMob(16)};
  height: ${convertToVwMob(16)};
  background-image: url("data:image/svg+xml,%3Csvg width='16' height='16' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M19 3H18V1H16V3H8V1H6V3H5C3.89 3 3 3.9 3 5V19C3 20.1 3.89 21 5 21H19C20.11 21 21 20.1 21 19V5C21 3.9 20.11 3 19 3ZM19 19H5V8H19V19Z' fill='%23676767'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  flex-shrink: 0;
`;

const DonorMessage = styled.p`
  font-size: ${convertToVwMob(16)};
  font-weight: 500;
  color: ${({ theme }) => theme.colors.grayScale[90]};
  line-height: 1.5;
  margin: 0;
`;

const AmountContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${convertToVwMob(8)};
`;

const AmountBox = styled.div`
  padding: ${convertToVwMob(10)} ${convertToVwMob(20)};
  border: 1px solid ${({ theme }) => theme.colors.grayScale[20]};
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  gap: ${convertToVwMob(4)};
`;

const AmountLabel = styled.span`
  font-size: ${convertToVwMob(16)};
  color: ${({ theme }) => theme.colors.grayScale[70]};
`;

const AmountValue = styled.span<{ color: 'main' | 'sub' }>`
  font-size: ${convertToVwMob(20)};
  font-weight: 500;
  color: ${({ theme, color }) => (color === 'main' ? theme.colors.grayScale[90] : theme.colors.grayScale[70])};
`;

const LoadingTextMob = styled.p`
  font-size: ${convertToVwMob(20)};
  color: ${({ theme }) => theme.colors.grayScale[50]};
  text-align: center;
  padding: ${convertToVwMob(40)} 0;
`;

export default PaymentStatusMob;
